import React, { useCallback, useEffect, useState } from 'react'

import Select from 'live/components/forms/ui/select'
import globalNotification from 'live/services/global-notification'
import moment from 'moment'

import styled from 'styled-components'

import { ModalField } from './components/ModalField'
import { getAlarmById, getLiveRigs, getLiveWells, getServerLocalTimeZone } from './api/api'
import { Alarm, ApprovalSettingsType, ScheduleType, SelectOptionsType } from './types/ApprovalSettings'
import { frequencyOptions, timespanOptions } from './utils/data'
import { HourField } from './components/HourField'
import { Notification } from './components/Notification'
import { ModalSchedulingAction } from '../src/SheduleActionType'
import { i18n } from '../approval-settings/utils/utils'

export function ModalApprovalSettings({
    data: approvalSettings,
    error,
    loaded,
    setData: setApprovalSettings,
    report,
    schedule
}: ModalSchedulingAction<ApprovalSettingsType>): JSX.Element {
    const [assetOptions, setAssetOptions] = useState<SelectOptionsType[]>([])
    const [selectedAsset, setSelectedAsset] = useState<SelectOptionsType[]>([])
    const [scheduleType, setScheduleType] = useState<ScheduleType>({} as ScheduleType)
    const [alarmInfo, setAlarmInfo] = useState<{ name: string; url: string }>({ name: '', url: '' })
    const [serverLocalTime, setServerLocalTime] = useState<string>()

    function fetchAlarm(ruleId: string, approvalSettings: ApprovalSettingsType) {
        return getAlarmById(parseInt(ruleId))
            .then(res => {
                const TYPE = 'Alarm Rule'
                const formattedRule = res?.name + ' (ID - ' + ruleId.padStart(4, '0') + ')'

                setApprovalSettings({ ...approvalSettings, alertRuleName: res?.name })
                setAlarmInfo({ name: res?.name, url: `${res?.status?.url}/edit` })
                return { ...res, type: TYPE, alertRule: formattedRule }
            })
            .catch(reject => console.error('Erro - ', reject))
    }

    const getLiveAssets = useCallback(() => {
        Promise.all([getLiveRigs(), getLiveWells()]).then(([liveRigs, liveWells]) => {
            let options = []

            options = options.concat(liveRigs.map(rig => ({ value: `${rig.id}-rig`, label: rig.name })))
            options = options.concat(liveWells.map(well => ({ value: `${well.id}-well`, label: well.name })))

            setAssetOptions(options)
        })
    }, [])

    useEffect(() => {
        if (loaded && report && schedule) {
            const approvalSettingsInitialState = {
                ...approvalSettings,
                reportId: report.uid,
                reportName: report.title,
                alertRuleId: schedule.ruleId,
                scheduleId: schedule.uuid,
                scheduleName: schedule.name,
                recipientGroupId: schedule.recipientsUid,
                isScheduleActive: schedule.active,
                isApprovalSettingsValid: !report.isDraft && schedule.active && schedule.isRuleAlarm,
                /*
                    The calculation inside minute() is necessary because
                    th options have a minute interval and the value is only selected automatically
                    if the current minute is available in the options, otherwise no time is selected and
                    therefore the value is saved as empty in the db
                */
                hour:
                    approvalSettings.hour === ''
                        ? moment()
                              .minute((2 * Math.round(moment().minute() / 2)) % 60)
                              .startOf('minutes')
                              .format('LT')
                        : approvalSettings.hour
            }
            setApprovalSettings({ ...approvalSettingsInitialState })

            if (approvalSettings?.assets?.length > 0 && assetOptions?.length > 0) {
                setSelectedAsset(assetOptions.filter(opt => approvalSettings.assets.includes(opt.value)))
            }

            fetchAlarm(schedule?.ruleId, approvalSettingsInitialState)
                .then((alarm: Alarm) => setScheduleType({ type: alarm?.type, alertRule: alarm?.alertRule }))
                .catch(error => console.error(error))
        }
    }, [loaded, assetOptions, report, schedule])

    useEffect(() => {
        setApprovalSettings({ ...approvalSettings, assets: selectedAsset.map(asset => asset.value) })
    }, [selectedAsset])

    useEffect(() => {
        let intervalId

        getLiveAssets()
        getServerLocalTimeZone().then(res => {
            intervalId = setInterval(() => {
                const convertedDate = new Date().toLocaleString('en-US', { timeZone: res })
                setServerLocalTime(moment(convertedDate).format('LTS'))
            }, 1000)
        })

        return () => {
            clearInterval(intervalId)
        }
    }, [])

    return (
        <Body>
            {error ? (
                globalNotification.error(
                    <Notification title={i18n('Error trying to get the approval settings')}>{error}</Notification>,
                    20
                )
            ) : !loaded ? (
                <Loading style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '464px' }}>
                    {i18n('Loading')}
                </Loading>
            ) : (
                <StyledApprovalSettings>
                    <StyledScheduleTypeContainer>
                        <span>{i18n('Schedule Type')}</span>
                        <StyledSettingsRuleInfoContent>
                            <StyledTypeAndAlertRule>
                                <ModalField label="Type" field={<span>{scheduleType?.type}</span>} />
                                <ModalField label="Alert Rule" field={<span>{scheduleType?.alertRule}</span>} />
                            </StyledTypeAndAlertRule>
                            <StyledRuleInfo>
                                <i className="far fa-info-circle" />
                                <div className="rule-info-content">
                                    <span className="rule-info-title">{i18n('Check alert rule')}</span>
                                    <p>
                                        {' '}
                                        {i18n('The')}
                                        <a href={alarmInfo.url} target="_blank">
                                            {alarmInfo.name}
                                        </a>{' '}
                                        {i18n('alert rule must start with the event type')}
                                        <code>__report_aproval_event</code>. <br />
                                        {i18n(
                                            'The pipes expression must contain the necessary fields for the selected report, as shown below'
                                        )}
                                        <br />
                                        <code>
                                            __report_approval_event <br />
                                            {`=> @set newmap('asset_name_event_type',(assetId, assetType):seq) as __report_lookup,timespan as __report_span`}
                                        </code>
                                    </p>
                                </div>
                            </StyledRuleInfo>
                        </StyledSettingsRuleInfoContent>
                    </StyledScheduleTypeContainer>

                    <StyledReportApprovalContainer>
                        <span>{i18n('Report Approval Settings')}</span>
                        <StyledReportApprovalContent style={{ width: '100%' }}>
                            <StyledReportContentSource style={{ flex: 1 }}>
                                <StyledSettingsContent style={{ height: '100%' }}>
                                    <span style={{ fontSize: '14px', textTransform: 'uppercase' }}>
                                        {i18n('Report Content Source')}
                                    </span>
                                    <StyledAssetAndTimespan>
                                        <ModalField
                                            style={{ flex: 1 }}
                                            label="Asset"
                                            requiredField
                                            field={
                                                <>
                                                    <Select
                                                        style={{ maxWidth: '237px' }}
                                                        searchable
                                                        clearable
                                                        multi
                                                        options={assetOptions}
                                                        placeholder={i18n('Select')}
                                                        value={selectedAsset}
                                                        onChange={(_, option) => {
                                                            setSelectedAsset(option)
                                                        }}
                                                        id="select-asset"
                                                    />
                                                </>
                                            }
                                        />
                                        <div className="field-and-message">
                                            <ModalField
                                                label="Timespan"
                                                requiredField
                                                field={
                                                    <Select
                                                        searchable
                                                        clearable={false}
                                                        options={timespanOptions}
                                                        placeholder={i18n('Select')}
                                                        value={approvalSettings?.timespan}
                                                        onChange={option => {
                                                            setApprovalSettings({
                                                                ...approvalSettings,
                                                                timespan: option
                                                            })
                                                        }}
                                                        id="select-timespan"
                                                    />
                                                }
                                            />
                                            <div className="icon-message">
                                                <i className="far fa-info-circle" />
                                                <span>
                                                    {i18n(
                                                        'Timespan delimits successive start and end dates, linked to the configured recurrence.'
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                    </StyledAssetAndTimespan>
                                </StyledSettingsContent>
                            </StyledReportContentSource>

                            <StyledRecurrenceContainer>
                                <StyledSettingsContent>
                                    <span style={{ fontSize: '14px', textTransform: 'uppercase' }}>
                                        {i18n('Recurrence')}
                                    </span>
                                    <ModalField
                                        label="Frequency"
                                        requiredField
                                        field={
                                            <>
                                                <Select
                                                    style={{ maxWidth: '237px' }}
                                                    searchable
                                                    clearable={false}
                                                    options={frequencyOptions}
                                                    placeholder={i18n('Select')}
                                                    value={approvalSettings?.frequency}
                                                    onChange={option => {
                                                        setApprovalSettings({
                                                            ...approvalSettings,
                                                            frequency: option
                                                        })
                                                    }}
                                                    id="select-frequency"
                                                />
                                            </>
                                        }
                                    />
                                    <ModalField
                                        label="Hour"
                                        requiredField
                                        field={
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <HourField
                                                    hour={approvalSettings?.hour}
                                                    setHour={currentHour =>
                                                        setApprovalSettings({ ...approvalSettings, hour: currentHour })
                                                    }
                                                />
                                                {serverLocalTime && (
                                                    <div
                                                        className="icon-message"
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '4px',
                                                            marginTop: '8px'
                                                        }}
                                                    >
                                                        <i className="far fa-info-circle" />
                                                        <span>{i18n('Server local time') + serverLocalTime}</span>
                                                    </div>
                                                )}
                                            </div>
                                        }
                                    />
                                </StyledSettingsContent>
                            </StyledRecurrenceContainer>
                        </StyledReportApprovalContent>
                    </StyledReportApprovalContainer>
                </StyledApprovalSettings>
            )}
        </Body>
    )
}

const Body = styled.div`
    display: flex;
    justify-content: center;
`

const Loading = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 464px;
`

const StyledApprovalSettings = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 32px;
`

const StyledScheduleTypeContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;

    span {
        font-size: 16px;
        font-weight: 700;
    }
`

const StyledReportApprovalContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;

    span {
        font-size: 16px;
        font-weight: 700;
    }
`

const StyledReportApprovalContent = styled.div`
    display: flex;
    gap: 24px;
`

const StyledRecurrenceContainer = styled.div`
    display: flex;
    flex-direction: column;
`

const StyledReportContentSource = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;

    span {
        font-size: 16px;
        font-weight: 700;
    }
`

const StyledTypeAndAlertRule = styled.div`
    display: flex;
    gap: 48px;
`

const StyledRuleInfo = styled.div`
    display: flex;
    gap: 8px;
    width: 626px;
    padding: 16px;

    border: 1px solid var(--blue-500);
    border-radius: 8px;

    background-color: var(--blue-200);

    i {
        margin-top: 4px;
        color: var(--blue-500);
    }

    .rule-info-content {
        display: flex;
        flex-direction: column;

        .rule-info-title {
            font-weight: 600;

            color: var(--blue-500);
        }

        p {
            margin-top: 8px;
            line-height: 24px;

            a {
                text-decoration: underline;
                color: var(--blue-500);
            }

            code {
                font-family: 'Source Code Pro', monospace;
                font-size: 13px;
                font-weight: 400;
                letter-spacing: 1px;
            }
        }
    }
`

const StyledAssetAndTimespan = styled.div`
    display: flex;
    gap: 16px;
    width: 100%;

    .field-and-message {
        flex-direction: column;

        .icon-message {
            display: flex;
            margin-top: 8px;

            span {
                width: 250px;
                font-size: 12px !important;
                font-weight: 400 !important;
                line-height: 14.5px;

                margin-left: 4px;
            }

            i {
                margin-top: 3px;
            }
        }
    }
`

const StyledSettingsRuleInfoContent = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 16px 24px;
    gap: 8px;

    border: 1px solid var(--gray-500);
    border-radius: 3px;
`

const StyledSettingsContent = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    padding: 16px 24px;
    gap: 20px;

    border: 1px solid var(--gray-500);
    border-radius: 3px;
`
